import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabaseClient';
import { Plus, Search, Filter, Edit, Trash2, Eye, Calendar, DollarSign, Users, Download, RefreshCw } from 'lucide-react';
import SubmissionModal from './SubmissionModal';
import SubmissionViewModal from './SubmissionViewModal';
import LoadingSpinner from '../../ui/LoadingSpinner';
import '../../../styles/SubmissionsAdmin.css';

const SubmissionsAdmin = () => {
  const [submissions, setSubmissions] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [collectionFilter, setCollectionFilter] = useState('all');

  const [sortBy, setSortBy] = useState('submission_id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(null);

  // Fetch submissions data
  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      
      // Calculate range for pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      // Get total count first
      if (currentPage === 1) {
        try {
          let countQuery = supabase
            .from('submissions_details')
            .select('*', { count: 'exact', head: true });

          if (searchQuery) {
            countQuery = countQuery.or(`title.ilike.%${searchQuery}%`);
          }
          if (galleryFilter !== 'all') {
            countQuery = countQuery.eq('gallery_id', galleryFilter);
          }
          if (collectionFilter !== 'all') {
            countQuery = countQuery.eq('collection_id', collectionFilter);
          }

          const { count, error: countError } = await countQuery;
          if (countError) {
            console.error('Count query error:', countError);
            setTotalCount(0);
          } else {
            setTotalCount(count || 0);
          }
        } catch (countError) {
          console.error('Count query exception:', countError);
          setTotalCount(0);
        }
      }

      // Fetch paginated submissions with basic data first
      let query = supabase
        .from('submissions_details')
        .select('*')
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(from, to);

      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%`);
      }
      if (galleryFilter !== 'all') {
        query = query.eq('gallery_id', galleryFilter);
      }
      if (collectionFilter !== 'all') {
        query = query.eq('collection_id', collectionFilter);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Submissions data:', data);
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch galleries and collections for filters
  const fetchFilterData = async () => {
    try {
      console.log('Fetching galleries and collections...');
      
      const galleriesResult = await supabase
        .from('galleries')
        .select('gallery_id, name, address')
        .order('name');
      
      const collectionsResult = await supabase
        .from('collections')
        .select('collection_id, title, description')
        .in('type', ['collection', 'show', 'event']) // Only show collections with these types
        .order('title');

      console.log('Galleries result:', galleriesResult);
      console.log('Collections result:', collectionsResult);

      if (galleriesResult.error) {
        console.error('Galleries error:', galleriesResult.error);
      }
      if (collectionsResult.error) {
        console.error('Collections error:', collectionsResult.error);
      }

      setGalleries(galleriesResult.data || []);
      setCollections(collectionsResult.data || []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    // Test if submissions_details table exists
    const testTable = async () => {
      try {
        const { data, error } = await supabase
          .from('submissions_details')
          .select('submission_id')
          .limit(1);
        
        console.log('Table test result:', { data, error });
        
        if (error) {
          console.error('Table access error:', error);
        }
      } catch (err) {
        console.error('Table test exception:', err);
      }
    };
    
    testTable();
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [searchQuery, galleryFilter, collectionFilter, sortBy, sortOrder, currentPage, pageSize]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Handle filters
  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case 'gallery':
        setGalleryFilter(value);
        break;
      case 'collection':
        setCollectionFilter(value);
        break;
      default:
        break;
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Handle submission actions
  const handleCreateSubmission = () => {
    setEditingSubmission(null);
    setShowCreateModal(true);
  };

  const handleEditSubmission = (submission) => {
    setEditingSubmission(submission);
    setShowEditModal(true);
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowViewModal(true);
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('submissions_details')
          .delete()
          .eq('submission_id', submissionId);

        if (error) throw error;

        // Refresh data
        fetchSubmissions();
      } catch (error) {
        console.error('Error deleting submission:', error);
        alert('Failed to delete submission');
      }
    }
  };

  // Get status badge styling - based on featured and deadline
  const getStatusBadge = (submission) => {
    if (submission.featured) {
      return (
        <span className="submissions-status-badge featured">
          Featured
        </span>
      );
    }
    
    if (submission.submission_deadline) {
      const deadline = new Date(submission.submission_deadline);
      const now = new Date();
      
      if (deadline < now) {
        return (
          <span className="submissions-status-badge expired">
            Expired
          </span>
        );
      } else if (deadline.getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000) { // 7 days
        return (
          <span className="submissions-status-badge ending-soon">
            Ending Soon
          </span>
        );
      } else {
        return (
          <span className="submissions-status-badge active">
            Active
          </span>
        );
      }
    }
    
    return (
      <span className="submissions-status-badge active">
        Active
      </span>
    );
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && submissions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading submissions..." />
      </div>
    );
  }

  return (
    <div className="submissions-admin-container">
      <div className="submissions-admin-content">

        {/* Stats Cards */}
        <div className="submissions-stats-grid">
          <div className="submissions-stat-card">
            <div className="flex items-center">
              <div className="submissions-stat-icon-wrapper blue">
                <Calendar className="submissions-stat-icon blue" />
              </div>
              <div className="ml-4">
                <p className="submissions-stat-label">Total Submissions</p>
                <p className="submissions-stat-value">{totalCount}</p>
              </div>
            </div>
          </div>

          <div className="submissions-stat-card">
            <div className="flex items-center">
              <div className="submissions-stat-icon-wrapper green">
                <Users className="submissions-stat-icon green" />
              </div>
              <div className="ml-4">
                <p className="submissions-stat-label">Featured Calls</p>
                <p className="submissions-stat-value">
                  {submissions.filter(s => s.featured).length}
                </p>
              </div>
            </div>
          </div>

          <div className="submissions-stat-card">
            <div className="flex items-center">
              <div className="submissions-stat-icon-wrapper purple">
                <DollarSign className="submissions-stat-icon purple" />
              </div>
              <div className="ml-4">
                <p className="submissions-stat-label">Total Revenue</p>
                <p className="submissions-stat-value">
                  ${submissions.reduce((sum, s) => sum + (parseFloat(s.submission_price) || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="submissions-stat-card">
            <div className="flex items-center">
              <div className="submissions-stat-icon-wrapper orange">
                <Eye className="submissions-stat-icon orange" />
              </div>
              <div className="ml-4">
                <p className="submissions-stat-label">Expired Calls</p>
                <p className="submissions-stat-value">
                  {submissions.filter(s => {
                    if (!s.submission_deadline) return false;
                    return new Date(s.submission_deadline) < new Date();
                    }).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="submissions-controls">
          <div className="submissions-controls-content">
            {/* Search */}
            <form onSubmit={handleSearch} className="submissions-search-form">
              <div className="relative">
                <Search className="submissions-search-icon" />
                <input
                  type="text"
                  placeholder="Search submissions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="submissions-search-input"
                />
              </div>
            </form>

            {/* Filters */}
            <div className="submissions-filters">
              <select
                value={galleryFilter}
                onChange={(e) => handleFilterChange('gallery', e.target.value)}
                className="submissions-filter-select"
              >
                <option value="all">All Galleries</option>
                {galleries.map(gallery => (
                  <option key={gallery.gallery_id} value={gallery.gallery_id}>
                    {gallery.name}
                  </option>
                ))}
              </select>

              <select
                value={collectionFilter}
                onChange={(e) => handleFilterChange('collection', e.target.value)}
                className="submissions-filter-select"
              >
                <option value="all">All Collections</option>
                {collections.map(collection => (
                  <option key={collection.collection_id} value={collection.collection_id}>
                    {collection.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Create Button */}
            <button
              onClick={handleCreateSubmission}
              className="submissions-create-button"
            >
              <Plus className="w-5 h-5" />
              <span>Create Submission</span>
            </button>

            {/* Action Buttons */}
            <div className="submissions-action-buttons">
              <button className="submissions-action-btn submissions-action-btn-secondary">
                <Download className="w-4 h-4" />
                Export
              </button>
              <button 
                onClick={fetchSubmissions}
                className="submissions-action-btn submissions-action-btn-primary"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="submissions-table-container">
          <div className="overflow-x-auto">
            <table className="submissions-table">
              <thead className="submissions-table-header">
                <tr>
                  <th className="submissions-table-header sortable" onClick={() => handleSort('title')}>
                    <div className="flex items-center space-x-1">
                      <span>Title</span>
                      {sortBy === 'title' && (
                        <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="submissions-table-header">Gallery</th>
                  <th className="submissions-table-header">Collection</th>
                  <th className="submissions-table-header sortable" onClick={() => handleSort('submission_price')}>
                    <div className="flex items-center space-x-1">
                      <span>Price</span>
                      {sortBy === 'submission_price' && (
                        <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="submissions-table-header sortable" onClick={() => handleSort('submission_deadline')}>
                    <div className="flex items-center space-x-1">
                      <span>Deadline</span>
                      {sortBy === 'submission_deadline' && (
                        <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="submissions-table-header">Featured</th>
                  <th className="submissions-table-header sortable" onClick={() => handleSort('submission_id')}>
                     <div className="flex items-center space-x-1">
                       <span>ID</span>
                       {sortBy === 'submission_id' && (
                         <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                       )}
                     </div>
                   </th>
                  <th className="submissions-table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="submissions-table-body">
                {submissions.map((submission) => (
                  <tr 
                    key={submission.submission_id} 
                    className="submissions-table-row clickable"
                    onClick={() => handleViewSubmission(submission)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="submissions-title-cell">{submission.title}</div>
                        <div className="submissions-id-text">ID: {submission.submission_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="submissions-gallery-name">
                        {galleries.find(g => g.gallery_id === submission.gallery_id)?.name || 'N/A'}
                      </div>
                      <div className="submissions-gallery-location">
                        {galleries.find(g => g.gallery_id === submission.gallery_id)?.address || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="submissions-collection-title">
                        {collections.find(c => c.collection_id === submission.collection_id)?.title || 'N/A'}
                      </div>
                      <div className="submissions-collection-description">
                        {collections.find(c => c.collection_id === submission.collection_id)?.description || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="submissions-price">
                        ${submission.submission_price ? parseFloat(submission.submission_price).toFixed(2) : '0.00'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="submissions-deadline">
                        {submission.submission_deadline ? new Date(submission.submission_deadline).toLocaleDateString() : 'No deadline'}
                      </div>
                    </td>
                                         <td className="px-6 py-4 whitespace-nowrap">
                       {getStatusBadge(submission)}
                     </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       {submission.submission_id}
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="submissions-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewSubmission(submission);
                          }}
                          className="submissions-action-button view"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSubmission(submission);
                          }}
                          className="submissions-action-button edit"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSubmission(submission.submission_id);
                          }}
                          className="submissions-action-button delete"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {submissions.length === 0 && !loading && (
            <div className="submissions-empty-state">
              <Calendar className="submissions-empty-icon" />
              <h3 className="submissions-empty-title">No submissions</h3>
              <p className="submissions-empty-description">
                {searchQuery || galleryFilter !== 'all' || collectionFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by creating a new submission call.'}
              </p>
              {!searchQuery && galleryFilter === 'all' && collectionFilter === 'all' && (
                <div className="mt-6">
                  <button
                    onClick={handleCreateSubmission}
                    className="submissions-create-button"
                  >
                    Create Submission
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="submissions-pagination">
            <div className="submissions-pagination-info">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} submissions
            </div>
            
            <div className="submissions-pagination-controls">
              <div className="submissions-page-size-control">
                <label htmlFor="page-size" className="submissions-page-size-label">Show:</label>
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                  className="submissions-page-size-select"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              
              <div className="submissions-pagination-buttons">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="submissions-pagination-button"
                >
                  Previous
                </button>
                
                <span className="submissions-pagination-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="submissions-pagination-button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <SubmissionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            fetchSubmissions();
          }}
          galleries={galleries}
          collections={collections}
        />
      )}

      {showEditModal && editingSubmission && (
        <SubmissionModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={() => {
            setShowEditModal(false);
            fetchSubmissions();
          }}
          submission={editingSubmission}
          galleries={galleries}
          collections={collections}
          isEditing={true}
        />
      )}

      {showViewModal && selectedSubmission && (
        <SubmissionViewModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          submission={selectedSubmission}
        />
      )}
    </div>
  );
};

export default SubmissionsAdmin;
